import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ChatClient } from "../client";
import type {
  AssistantPart,
  Message,
  ProposalPseudoMessage,
  TimelineEntry,
} from "../types";
import type { AvailableCommandEntry, ConnectedFrame, Frame } from "../events";
import {
  FrameKind,
  isAvailableCommands,
  type FactProposalFrame,
  type SkillProposalFrame,
} from "../events";
import type { FrameRegistry } from "../extensions/frameRegistry";
import { parseSSEStream } from "../sse";
import { useStreamingTurn, TurnStatus } from "./useStreamingTurn";
import type { ProposalsApi } from "../../api/proposalsApi";
import { resolvePageContext } from "../pageContext";

/** The browser's IANA timezone (e.g. "America/Los_Angeles") as a payload
 *  fragment, or {} when the platform can't report it. Sent on every turn so
 *  the agent reasons about time in the user's zone. */
export function resolveBrowserTz(): { tz?: string } {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? { tz } : {};
  } catch {
    return {};
  }
}

const log = (...args: unknown[]) =>
  // eslint-disable-next-line no-console
  console.warn("[useChatSession]", ...args);

/** A one-frame async stream that emits a single error frame. Lets a
 *  pre-stream failure (openChatStream throwing on a non-OK response)
 *  surface through the SAME reducer error path as an in-stream error. */
async function* errorStream(
  message: string,
): AsyncGenerator<Frame, void, void> {
  yield { kind: FrameKind.Error, message } as Frame;
}

export interface UseChatSessionArgs {
  client: ChatClient;
  sessionId: string | null;
  registry: FrameRegistry | null;
  onEvent?: (frame: Frame) => void;
  /** Optional. When present, the session hook backfills pending
   *  proposals on mount and surfaces `acceptProposal` / `rejectProposal`
   *  helpers that the inline ProposalCard invokes. Pages that don't
   *  need the proposals UX can omit it. */
  proposalsApi?: ProposalsApi | null;
  /** Called once per new chat when the backend's `connected` frame
   *  carries a `session_id`. ChatProvider wires this to `setSessionId`
   *  so subsequent turns reuse the same session instead of minting a
   *  new one per turn. Only fires when `sessionId` is null (new chat);
   *  ignored if a session is already active. */
  onSessionEstablished?: (id: string) => void;
  /** The user's picked model id, or "auto" for router-driven. Threaded onto
   *  EVERY turn-start path (send, regenerate, edit) so it's sent on every
   *  message — not just the first send. Sent verbatim: "auto" (explicit) tells
   *  the backend to run its cost-router classifier; a specific id is used as-is
   *  with no classification. Without this, regenerate/edit omitted the model and
   *  silently fell back to the router even when a specific model was selected. */
  selectedModel?: string;
}

export interface SendMessageArgs {
  text: string;
  model?: string;
  attachment_ids?: string[];
  /** Display info (id/name/mime) for the attached files, used to render chips
   *  on the optimistic user bubble. The backend persists the same so they
   *  survive reload. */
  attachments?: { id: string; name: string; mime?: string }[];
  /** Project to associate on session creation (first message only). */
  project_id?: string | null;
  /** "plan" enters blocking plan mode for this turn (composer Plan toggle).
   *  Sent as payload.mode; the backend gates execution until the user
   *  approves the plan. */
  mode?: "plan";
  /** Composer "Run in background" toggle. Sent as payload.background_subagents;
   *  the backend tells the model to spawn fire-and-forget (no wait), so the
   *  turn ends immediately and the work is tracked on the tasks page. */
  backgroundSubagents?: boolean;
  /** Org-settings fetch: constrain the turn to read MCP + Artie apply tools. */
  orgSync?: boolean;
}

export interface UseChatSessionResult {
  /** Real messages + proposal pseudo-messages, interleaved by
   *  insertion order. Consumers branch on entry.role to dispatch
   *  to MessageBubble vs ProposalCard. */
  history: TimelineEntry[];
  turn: ReturnType<typeof useStreamingTurn>;
  sendMessage: (text: string | SendMessageArgs) => Promise<void>;
  cancel: () => Promise<void>;
  /** Inject a mid-turn message into the live agent loop via POST /v1/chat/{session_id}/steer. */
  steer: (text: string) => Promise<void>;
  /** Tier 1 #2 — no-op until backend ships. */
  regenerate: () => Promise<void>;
  /** Tier 1 #2 — no-op until backend ships. */
  editAndResend: (messageId: string, newText: string) => Promise<void>;
  /** SP-3b: v2 plugin scripts the agent has surfaced via the
   *  `available_commands` SSE frame. Re-populated on every new turn
   *  (the frame fires once per session, right after `connected`). */
  availableCommands: AvailableCommandEntry[];
  /** Accept the pending proposal at proposal_id. Optimistically flips
   *  the timeline entry's status to "accepted"; reverts to "pending"
   *  if the API call fails. Throws on failure so the card can show
   *  a toast or surface the error. No-op if proposalsApi wasn't
   *  supplied to the hook. */
  acceptProposal: (proposal_id: string) => Promise<void>;
  /** Reject the pending proposal with an optional reason. Same
   *  optimistic behavior as acceptProposal. */
  rejectProposal: (proposal_id: string, reason?: string) => Promise<void>;
  /** Respond to a pending bot_confirmation by sending an affirmative user
   *  message. Reuses the chat send path — there is no dedicated approve
   *  endpoint. `summary` is woven into the reply for clarity. */
  confirmAction: (summary?: string) => Promise<void>;
  /** Decline a pending confirmation by sending a negative user message. */
  declineAction: () => Promise<void>;
  /** Decide a pending plan (blocking plan mode). Posts a plan_decision to
   *  /steer; the next turn applies it. `edited_todos` only for "edit". No-op
   *  without an active session. */
  decidePlan: (
    decision: "approve" | "edit" | "reject",
    opts?: { edited_todos?: unknown[]; reason?: string },
  ) => Promise<void>;
  /** Decide a paused turn's budget dimension (cost/time/iterations exceeded).
   *  Posts a budget_decision to /steer; "continue" resumes the loop, "stop"
   *  ends the turn. No-op without an active session. */
  decideBudget: (decision: "continue" | "stop") => Promise<void>;
  /** Decide the gated tool call in `turn.pendingReview` (spec §5.2).
   *
   *  QUEUES THE VERDICT ONLY — it does not resume the turn and returns no
   *  result. There is no resume endpoint: the backend parks the decision and
   *  `resume_pending_if_decided` consumes it at the top of the NEXT turn, so
   *  the tool executes when the user sends their next message. A caller that
   *  awaits this expecting the send to have happened is wrong.
   *
   *  Approve and reject only — `edit` is deliberately absent (spec §5.3, see
   *  `ChatClient.toolDecision`). Throws when the gate itself failed
   *  (`pendingReview.error`), because that decision could never be read.
   *  No-op without an active session or a pending review. */
  decideReview: (
    decision: "approve" | "reject",
    opts?: { reason?: string },
  ) => Promise<void>;
  /** Set or clear a thumbs feedback vote on an assistant message.
   *  Optimistically toggles the entry's `feedback`; reverts on API failure.
   *  Passing null clears the vote. No-op without an active session. */
  submitFeedback: (
    messageId: string,
    vote: "up" | "down" | null,
  ) => Promise<void>;
  /** Refetch persisted history and merge it in (deduped by id). Called when an
   *  out-of-band event signals new persisted content for this session — e.g. a
   *  background subagent completing after the parent turn ended. */
  reloadHistory: () => Promise<void>;
  /** True when the initial tail-load may have clipped older messages. */
  historyTruncated: boolean;
  /** Fetch the full history (no tail limit) and merge it into the timeline. */
  loadEarlier: () => Promise<void>;
}

export function useChatSession({
  client,
  sessionId,
  registry,
  onEvent,
  proposalsApi,
  onSessionEstablished,
  selectedModel,
}: UseChatSessionArgs): UseChatSessionResult {
  // Model sent on every turn-start path. The picker value is sent verbatim:
  // "auto" (explicit) → backend runs the cost-router classifier; a specific id
  // → used as-is, no classification. Defaults to "auto" when unset so a request
  // always carries a model field (removes the "absent vs auto" ambiguity).
  const modelOverride = selectedModel || "auto";
  const [history, setHistory] = useState<TimelineEntry[]>([]);
  // Session-switch tail size: newest N messages. Big enough that typical
  // sessions load whole; long ones clip and offer "Load earlier messages".
  const HISTORY_TAIL_LIMIT = 60;
  // True when the initial tail-load may have clipped older messages — the
  // message list offers "Load earlier messages" (loadEarlier) while set.
  const [historyTruncated, setHistoryTruncated] = useState(false);
  const [stream, setStream] = useState<AsyncGenerator<
    Frame,
    void,
    void
  > | null>(null);
  const [activeMessageId, setActiveMessageId] = useState<string | null>(null);
  // The live turn's stream. A new turn (send, regenerate, edit, budget
  // resume) or Stop aborts it: opened without a signal, every retry of a hung
  // review left the old connection open, the server still writing MBs into it
  // (ELASV1-3514). Unmount deliberately does NOT: the server runs the turn
  // inside the SSE generator, so closing the connection would kill a review
  // the user only stopped watching (closing V1's Artie panel, navigating
  // away). useStreamingTurn drains it without touching state instead.
  const streamAbortRef = useRef<AbortController | null>(null);
  const nextStreamSignal = useCallback((): AbortSignal => {
    streamAbortRef.current?.abort();
    const controller = new AbortController();
    streamAbortRef.current = controller;
    return controller.signal;
  }, []);
  const [availableCommands, setAvailableCommands] = useState<
    AvailableCommandEntry[]
  >([]);
  // Built-in commands (/mcp, ...) fetched once from GET /v1/commands. Held
  // in a ref so we can restore them on session change and also so the SSE
  // `available_commands` frame — which is authoritative but plugin-scoped
  // in older backends — doesn't wipe them.
  const builtinCommandsRef = useRef<AvailableCommandEntry[]>([]);

  // Seed the slash picker with built-in commands so `/` opens the palette
  // on a fresh chat before any turn has started (the SSE frame only fires
  // once a turn begins).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cmds = (await client.listCommands()) as AvailableCommandEntry[];
      if (cancelled || cmds.length === 0) return;
      builtinCommandsRef.current = cmds;
      setAvailableCommands((prev) => (prev.length === 0 ? cmds : prev));
    })();
    return () => {
      cancelled = true;
    };
  }, [client]);

  useEffect(() => {
    let cancelled = false;
    if (!sessionId) {
      // New chat. Clear history AND the live turn — otherwise a prior session's
      // completed turn (status/assistantParts/metrics) lingers, keeping
      // useIsEmptyChat false so the welcome screen never shows and a stale
      // metrics footer paints on the empty canvas.
      resetTurnRef.current();
      setHistory([]);
      // Reset to built-ins (not empty) so the picker keeps working on new chats.
      setAvailableCommands(builtinCommandsRef.current);
      return;
    }
    // If we just self-established this session id (mid-turn lift), skip the
    // reload. The optimistic user message + any streaming content already
    // reflect this session's state; the backend may not have persisted the
    // user message yet so getMessages could return [] and wipe the bubble.
    // Clear the ref so the very next re-render with the same id (e.g. user
    // clicks the session in the sidebar later) does a normal reload. Crucially
    // we do NOT reset the turn here — this is the SAME in-flight turn that
    // minted the id, and resetting would wipe its streamed reply.
    if (sessionId === establishedRef.current) {
      establishedRef.current = null;
      return;
    }
    // Switching to a different persisted session (sidebar click): drop the
    // previous session's live turn before loading the new one's history.
    resetTurnRef.current();
    (async () => {
      // Tail-load: newest HISTORY_TAIL_LIMIT messages only. Long sessions made
      // every sidebar switch fetch (and render) the entire history; older
      // messages load on demand via loadEarlier().
      const msgs = await client.getMessages(sessionId, {
        limit: HISTORY_TAIL_LIMIT,
      });
      if (cancelled) return;
      setHistoryTruncated(msgs.length >= HISTORY_TAIL_LIMIT);

      // Slice 2: backfill pending proposals scoped to this session so
      // reload surfaces inline cards in the same place they appeared
      // live. Fail-soft — if skills_service is unreachable we still
      // render the regular history.
      let proposals: ProposalPseudoMessage[] = [];
      if (proposalsApi) {
        try {
          const rows = await proposalsApi.list({ session_id: sessionId });
          proposals = rows.map((p) => ({
            id: p.proposal_id,
            session_id: sessionId,
            role: "proposal",
            kind: p.kind,
            payload: p.payload as Record<string, unknown>,
            status: "pending",
            trigger_kind: p.trigger_kind,
            created_at: p.created_at,
            expires_at: p.expires_at,
          }));
        } catch (exc) {
          log("proposal backfill failed", exc);
        }
      }

      // Sort by created_at. Some entries (e.g. backfilled proposals) may not
      // carry one — treat missing timestamps as "" so localeCompare stays safe
      // and they sort to the front rather than throwing.
      const merged: TimelineEntry[] = [...msgs, ...proposals].sort((a, b) =>
        (a.created_at ?? "").localeCompare(b.created_at ?? ""),
      );
      if (!cancelled) setHistory(merged);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, sessionId, proposalsApi]);

  // Refetch persisted history and merge it into the timeline, deduped by id.
  // Used when an out-of-band event (a background subagent completing after the
  // parent turn ended) signals new persisted content for this session: the
  // worker writes a SubagentCard message + fires the job_event NOTIFY, the chat
  // view reacts by calling this. Merge (not replace) so any optimistic/streamed
  // entries not yet persisted aren't dropped; server rows win on id collision.
  // Fetch the FULL history (no tail limit) and merge — used by the "Load
  // earlier messages" affordance when the initial tail-load clipped the top.
  const loadEarlier = useCallback(async () => {
    if (!sessionId) return;
    let msgs: TimelineEntry[];
    try {
      msgs = (await client.getMessages(sessionId)) as TimelineEntry[];
    } catch (exc) {
      log("loadEarlier failed", exc);
      return;
    }
    setHistoryTruncated(false);
    setHistory((prev) => {
      const serverIds = new Set(msgs.map((m) => m.id));
      const kept = prev.filter((e) => !serverIds.has(e.id));
      return [...kept, ...msgs].sort((a, b) =>
        (a.created_at ?? "").localeCompare(b.created_at ?? ""),
      );
    });
  }, [client, sessionId]);

  const reloadHistory = useCallback(async () => {
    if (!sessionId) return;
    let msgs: TimelineEntry[];
    try {
      msgs = (await client.getMessages(sessionId)) as TimelineEntry[];
    } catch (exc) {
      log("reloadHistory failed", exc);
      return;
    }
    setHistory((prev) => {
      const serverIds = new Set(msgs.map((m) => m.id));
      // A streamed assistant turn and its persisted DB row carry DIFFERENT
      // ids, so an id-only merge kept both — the completed turn rendered
      // twice after the push-triggered refetch. Key on role + extracted TEXT
      // (never raw JSON: the streamed blocks and the persisted row serialize
      // with different key order / extra fields, so stringify never matches).
      const textOf = (c: unknown): string => {
        if (typeof c === "string") return c;
        if (Array.isArray(c))
          return c
            .map((b) =>
              b &&
              typeof b === "object" &&
              (b as { type?: string }).type === "text"
                ? ((b as { text?: string }).text ?? "")
                : "",
            )
            .join("");
        return "";
      };
      const contentKey = (e: TimelineEntry) =>
        `${e.role}|${textOf((e as { content?: unknown }).content)}`;
      const serverKeys = new Set(
        msgs
          .filter((m) => textOf((m as { content?: unknown }).content))
          .map(contentKey),
      );
      // Keep prior entries the server doesn't (yet) know about (optimistic,
      // proposals, in-flight), then the authoritative server rows.
      // Content-dedupe ASSISTANT entries only: a streamed assistant turn and
      // its persisted row have different ids, so id-dedupe alone doubled the
      // turn. User/proposal entries keep id-only semantics — a user genuinely
      // re-sending identical text ("yes") mid-flight must never be dropped.
      const kept = prev.filter(
        (e) =>
          !serverIds.has(e.id) &&
          !(e.role === "assistant" && serverKeys.has(contentKey(e))),
      );
      return [...kept, ...msgs].sort((a, b) =>
        (a.created_at ?? "").localeCompare(b.created_at ?? ""),
      );
    });
  }, [client, sessionId]);

  // SP-3b: peel `available_commands` out of the frame stream into
  // session-scoped state, then forward to the consumer's onEvent.
  // The frame fires once per session right after `connected`, so we just
  // replace the cached list on each arrival.
  // Slice 2: similarly intercept fact_proposal / skill_proposal frames
  // and append a ProposalPseudoMessage to the timeline so the user
  // sees the card in-place.
  // Task 8 (simplified): steer() always appends optimistically (the user's
  // intent is always real). The steer_applied echo de-dups against the
  // last ~8 history entries — enough to cover the optimistic append plus
  // a few intervening tool frames. A genuine backend-only steer (no prior
  // optimistic) will not find a match in that window and will append.
  // This has no cross-turn contamination because there are no persistent
  // refs — only a slice of current history state.

  const handleFrame = useCallback(
    (frame: Frame) => {
      if (frame.kind === FrameKind.SteerApplied) {
        // The backend echo of a mid-turn steered message. De-dup against the
        // optimistic append by scanning a recent window of history (last 8
        // entries). If a matching user message is found, skip the append —
        // the optimistic already rendered it. Otherwise append (genuine
        // backend-only steer). Turn keeps streaming (no status change here).
        const text = (frame as { text?: string }).text ?? "";
        if (text) {
          setHistory((prev) => {
            const start = Math.max(0, prev.length - 8);
            const recent = prev.slice(start);
            const isMatch = (m: (typeof prev)[number]) =>
              m.role === "user" && (m as Message).content === text;
            // The optimistic bubble, still marked queued: it is applied now.
            const queuedIdx = recent.findIndex(
              (m) => isMatch(m) && (m as Message).queued,
            );
            if (queuedIdx >= 0) {
              const next = prev.slice();
              const { queued: _applied, ...applied } = prev[start + queuedIdx] as Message;
              next[start + queuedIdx] = applied;
              return next;
            }
            const dup = recent.some(isMatch);
            if (dup) return prev;
            return [
              ...prev,
              {
                id: crypto.randomUUID(),
                session_id: sessionId ?? "",
                role: "user",
                content: text,
                created_at: new Date().toISOString(),
              },
            ];
          });
        }
        onEvent?.(frame);
        return;
      }
      if (frame.kind === FrameKind.Connected) {
        // New chat: the server minted (or resolved) the real session_id and
        // sent it on `connected`. Lift it via onSessionEstablished so
        // subsequent turns POST to the same session instead of minting a
        // new one per turn.
        const established = (frame as ConnectedFrame).session_id;
        if (established && !sessionId) {
          // Record this id so the history-load effect can skip the
          // getMessages reload for this exact flip (the optimistic user
          // message hasn't been persisted yet; a reload would wipe it).
          establishedRef.current = established;
          onSessionEstablished?.(established);
        }
      }
      if (isAvailableCommands(frame)) {
        setAvailableCommands(frame.commands ?? []);
      } else if (
        frame.kind === FrameKind.FactProposal ||
        frame.kind === FrameKind.SkillProposal
      ) {
        const pf = frame as FactProposalFrame | SkillProposalFrame;
        const kind: "fact" | "skill" =
          frame.kind === FrameKind.FactProposal ? "fact" : "skill";
        const payload: Record<string, unknown> =
          kind === "fact"
            ? {
                category: (pf as FactProposalFrame).category,
                text: (pf as FactProposalFrame).text,
                evidence: (pf as FactProposalFrame).evidence,
              }
            : {
                name: (pf as SkillProposalFrame).name,
                description: (pf as SkillProposalFrame).description,
                body: (pf as SkillProposalFrame).body,
              };
        const now = new Date().toISOString();
        setHistory((prev) => {
          // De-duplicate: if a backfilled card already exists with the
          // same proposal_id, leave it (the live frame would have the
          // same payload anyway).
          if (prev.some((e) => e.id === pf.proposal_id)) return prev;
          const entry: ProposalPseudoMessage = {
            id: pf.proposal_id,
            session_id: sessionId ?? "",
            role: "proposal",
            kind,
            payload,
            status: "pending",
            trigger_kind: pf.trigger_kind ?? "agent_proposed",
            created_at: now,
            // Server-assigned; we'll get the real value on the next
            // backfill if the session reloads. ~7 days from now is the
            // current backend default.
            expires_at: new Date(Date.now() + 7 * 86400 * 1000).toISOString(),
          };
          return [...prev, entry];
        });
      }
      onEvent?.(frame);
    },
    [onEvent, onSessionEstablished, sessionId],
  );

  const turn = useStreamingTurn({ stream, registry, onEvent: handleFrame });

  // Hold the latest turn.reset so the history-load effect (declared above
  // `turn`, mirroring how it already reads establishedRef) can clear the live
  // turn on a session switch without taking `turn` as an effect dependency.
  // reset is stable (useCallback []), so the ref simply keeps the reference
  // handy across renders.
  const resetTurnRef = useRef(turn.reset);
  resetTurnRef.current = turn.reset;
  const turnMarkCancelledRef = useRef(turn.markCancelled);
  turnMarkCancelledRef.current = turn.markCancelled;

  // Same ref pattern for beginTurn: the turn-opening callbacks (sendMessage,
  // regenerate, editAndResend, budget-continue) call it to flip the turn to
  // Connecting the instant the user acts — so the Bolt shows its thinking pose
  // immediately instead of waiting for the backend's `connected` frame. Stable
  // (useCallback []), so the ref just keeps the reference handy without pulling
  // `turn` into those callbacks' dependency arrays.
  const beginTurnRef = useRef(turn.beginTurn);
  beginTurnRef.current = turn.beginTurn;

  // The live turn, for callbacks that must read it WITHOUT taking it as a
  // dependency (which would re-create them on every streamed chunk).
  // decideBudget needs the paused turn's parts at click time.
  const turnRef = useRef(turn);
  turnRef.current = turn;

  // Guard: when WE lift a session_id via onSessionEstablished mid-turn, the
  // history-load effect re-runs with the new sessionId. But the backend may not
  // have persisted our user message yet, so getMessages returns [] — wiping the
  // optimistic user bubble. Track the id we just established and skip the
  // reload for that one re-run. On any subsequent explicit re-open (sidebar
  // click etc.) establishedRef is null → normal reload proceeds.
  const establishedRef = useRef<string | null>(null);

  const lastPromotedRef = useRef<AssistantPart[] | null>(null);

  useEffect(() => {
    const hasContent =
      turn.assistantParts.length > 0 ||
      !!turn.thinkingText ||
      turn.workingNotes.length > 0 ||
      turn.toolCalls.length > 0;
    if (
      turn.status === TurnStatus.Complete &&
      hasContent &&
      turn.assistantParts !== lastPromotedRef.current
    ) {
      lastPromotedRef.current = turn.assistantParts;
      setHistory((prev) => [
        ...prev,
        {
          id: `assistant-${Date.now()}-${prev.length}`,
          session_id: sessionId ?? "",
          role: "assistant",
          content: turn.assistantParts,
          created_at: new Date().toISOString(),
          citations: turn.citations.length > 0 ? turn.citations : undefined,
          thinking: turn.thinkingText || undefined,
          thinkingMs: turn.thinkingMs,
          toolCalls: turn.toolCalls.length > 0 ? turn.toolCalls : undefined,
          workingNotes:
            turn.workingNotes.length > 0 ? turn.workingNotes : undefined,
          workLog: turn.workLog.length > 0 ? turn.workLog : undefined,
          durationMs: turn.metrics?.total_latency_ms,
        },
      ]);
    } else if (turn.status !== TurnStatus.Complete) {
      lastPromotedRef.current = null;
    }
  }, [
    turn.status,
    turn.assistantParts,
    sessionId,
    turn.citations,
    turn.workingNotes,
    turn.workLog,
    turn.metrics,
  ]);

  /** Append a budget-paused turn's content to history directly. The paused
   *  turn is held out of Complete, so the promote effect never sees it, and the
   *  next beginTurn() resets it away in the same batch (see openResumeTurn). */
  const promoteHeldTurn = useCallback((sid: string) => {
    const paused = turnRef.current;
    if (paused.assistantParts.length === 0) return;
    setHistory((prev) => [
      ...prev,
      {
        id: `assistant-${Date.now()}-${prev.length}`,
        session_id: sid,
        role: "assistant",
        content: paused.assistantParts,
        created_at: new Date().toISOString(),
        citations: paused.citations.length > 0 ? paused.citations : undefined,
        thinking: paused.thinkingText || undefined,
        thinkingMs: paused.thinkingMs,
        toolCalls: paused.toolCalls.length > 0 ? paused.toolCalls : undefined,
        workingNotes:
          paused.workingNotes.length > 0 ? paused.workingNotes : undefined,
        workLog: paused.workLog.length > 0 ? paused.workLog : undefined,
        durationMs: paused.metrics?.total_latency_ms,
      },
    ]);
  }, []);

  // A send is resolving a budget pause as Stop (see sendMessage).
  const budgetStopInFlightRef = useRef(false);

  const sendMessage = useCallback(
    async (input: string | SendMessageArgs) => {
      const args = typeof input === "string" ? { text: input } : input;
      // A new message while a turn is held at a budget pause is a new turn:
      // the user chose it over Continue. Resolve the pause as Stop first,
      // keeping what the paused turn made. Before this the composer sent it
      // to /steer (the held turn still looked busy) and the server queued it
      // for a turn that never started (ELASV1-3514).
      //
      // Once per pause: the pause stays set until a turn begins, so a second
      // send while the first awaits its stop would promote the paused turn
      // into history again (and post a second stop).
      if (
        turnRef.current.budgetPause &&
        sessionId &&
        !budgetStopInFlightRef.current
      ) {
        budgetStopInFlightRef.current = true;
        promoteHeldTurn(sessionId);
        try {
          await client.budgetDecision(sessionId, "stop");
        } catch (e) {
          // Fail open: the user's message matters more than the parked
          // decision, which the new turn supersedes anyway.
          console.warn("[useChatSession] budget stop before send failed", e);
        } finally {
          // beginTurn, below with no await before it, clears the pause.
          budgetStopInFlightRef.current = false;
        }
      }
      const message_id = crypto.randomUUID();
      setActiveMessageId(message_id);

      // Optimistically append the user's message so it appears immediately.
      // We use the same message_id as the row id (the backend now honors
      // client-supplied UUIDs in _persist_message) so subsequent edits can
      // target it. For a brand-new chat (sessionId is null) the row's
      // session_id is back-filled empty — the SSE `connected` frame carries
      // the real session_id which handleFrame lifts via onSessionEstablished
      // so subsequent turns reuse the same session.
      setHistory((prev) => [
        ...prev,
        {
          id: message_id,
          session_id: sessionId ?? "",
          role: "user",
          content: args.text,
          created_at: new Date().toISOString(),
          ...(args.attachments && args.attachments.length
            ? { attachments: args.attachments }
            : {}),
        },
      ]);

      // Optimistically start the turn so the thinking Bolt appears the instant
      // the user hits send — before the network round-trip to the backend's
      // `connected` frame. Dispatched synchronously with the history append so
      // the user bubble and the thinking Bolt render together.
      beginTurnRef.current();

      const signal = nextStreamSignal();
      try {
        const resp = await client.openChatStream({
          text: args.text,
          session_id: sessionId ?? undefined,
          message_id,
          model: args.model ?? modelOverride,
          project_id: args.project_id,
          attachment_ids: args.attachment_ids,
          // Always send the browser's IANA timezone so the agent's sense of
          // "now" (and any wall-clock reasoning / scheduling) uses the user's
          // local zone, not the server's UTC. Merged with mode when planning.
          payload: {
            ...resolveBrowserTz(),
            // Host platform's current page (side-panel deployment), when a
            // provider is registered. No-op {} in the test harness.
            ...resolvePageContext(),
            ...(args.mode === "plan" ? { mode: "plan" } : {}),
            ...(args.backgroundSubagents ? { background_subagents: true } : {}),
            ...(args.orgSync ? { org_sync: true } : {}),
          },
        }, signal);
        // A later turn superseded this one while it was opening.
        if (signal.aborted) return;
        const gen = parseSSEStream(resp.body!);
        setStream(gen);
      } catch (e) {
        if (signal.aborted) return;
        const msg =
          e instanceof Error ? e.message : "Failed to start the turn.";
        setStream(errorStream(msg));
      }
    },
    [client, sessionId, modelOverride, promoteHeldTurn, nextStreamSignal],
  );

  const cancel = useCallback(async () => {
    try {
      if (activeMessageId) await client.cancel(activeMessageId);
    } finally {
      // Close the connection too: the server's cancelled frame would arrive
      // on it, so end the turn as Cancelled here (only a live one).
      const live = turnRef.current.status;
      if (
        live === TurnStatus.Connecting ||
        live === TurnStatus.Preparing ||
        live === TurnStatus.Streaming
      ) {
        turnMarkCancelledRef.current();
      }
      streamAbortRef.current?.abort();
    }
  }, [client, activeMessageId]);

  const steer = useCallback(
    async (text: string) => {
      if (!sessionId) return;
      // Task 8 (simplified): the user's intent is always real — append
      // unconditionally. Two steer("retry") calls are two distinct intents
      // and each must appear in the timeline. The steer_applied echo handler
      // de-dups against recent history so no duplicate is rendered.
      const id = crypto.randomUUID();
      setHistory((prev) => [
        ...prev,
        {
          id,
          session_id: sessionId ?? "",
          role: "user",
          content: text,
          created_at: new Date().toISOString(),
          // Until the turn reads it (steer_applied clears this).
          queued: true,
        },
      ]);
      try {
        await client.steer(sessionId, text);
      } catch (e) {
        // The server never took it, so no steer_applied will clear `queued`:
        // the bubble would say "queued" forever. Drop it and rethrow; the
        // composer keeps the text for a retry.
        setHistory((prev) => prev.filter((m) => m.id !== id));
        throw e;
      }
    },
    [client, sessionId],
  );

  const regenerate = useCallback(async () => {
    // Parity gap #2: re-run the last assistant reply on the current branch.
    // The server pulls the last user message off the branch on regenerate=true,
    // so we don't need to send text — it's reused verbatim. The new turn
    // gets a fresh message_id (the regenerated assistant reply will sit at a
    // new causal_path child under the original user message).
    if (!sessionId) return;
    // Drop the trailing assistant reply(ies) so the promotion effect appends
    // the regenerated reply in-place rather than duplicating it.
    setHistory((prev) => {
      let lastUserIdx = -1;
      for (let i = prev.length - 1; i >= 0; i--) {
        if (prev[i].role === "user") {
          lastUserIdx = i;
          break;
        }
      }
      return lastUserIdx >= 0 ? prev.slice(0, lastUserIdx + 1) : prev;
    });
    const message_id = crypto.randomUUID();
    setActiveMessageId(message_id);
    // Show the thinking Bolt immediately (same rationale as sendMessage).
    beginTurnRef.current();
    const signal = nextStreamSignal();
    try {
      const resp = await client.openChatStream({
        text: "",
        session_id: sessionId,
        message_id,
        regenerate: true,
        model: modelOverride,
      }, signal);
      if (signal.aborted) return;
      const gen = parseSSEStream(resp.body!);
      setStream(gen);
    } catch (e) {
      if (signal.aborted) return;
      const msg = e instanceof Error ? e.message : "Failed to start the turn.";
      setStream(errorStream(msg));
    }
  }, [client, sessionId, modelOverride, nextStreamSignal]);

  const editAndResend = useCallback(
    async (messageId: string, newText: string) => {
      // Parity gap #2: branch under the targeted user message and run a
      // new turn whose user text replaces the original. The server allocates
      // a child causal_path under `messageId` and threads branch-scoped
      // history into the model. Locally, we slice history back to just
      // before the edited message so the UI doesn't show the now-stale
      // tail; the server-side branch holds the real history of both
      // versions for later switching.
      if (!sessionId) return;
      setHistory((prev) => {
        const idx = prev.findIndex((m) => m.id === messageId);
        if (idx < 0) return prev;
        const target = prev[idx];
        // Only real Message rows have `content`; refuse to edit a
        // ProposalPseudoMessage (the caller shouldn't ever try this,
        // but the type guard keeps the timeline well-formed).
        if (target.role === "proposal") return prev;
        return [
          ...prev.slice(0, idx),
          {
            ...(target as Message),
            content: newText,
            created_at: new Date().toISOString(),
          },
        ];
      });
      const message_id = crypto.randomUUID();
      setActiveMessageId(message_id);
      // Show the thinking Bolt immediately (same rationale as sendMessage).
      beginTurnRef.current();
      const signal = nextStreamSignal();
      try {
        const resp = await client.openChatStream({
          text: newText,
          session_id: sessionId,
          message_id,
          edit_message_id: messageId,
          model: modelOverride,
        }, signal);
        if (signal.aborted) return;
        const gen = parseSSEStream(resp.body!);
        setStream(gen);
      } catch (e) {
        if (signal.aborted) return;
        const msg =
          e instanceof Error ? e.message : "Failed to start the turn.";
        setStream(errorStream(msg));
      }
    },
    [client, sessionId, modelOverride, nextStreamSignal],
  );

  const confirmAction = useCallback(
    async (summary?: string) => {
      const text = summary
        ? `Yes, please proceed: ${summary}`
        : "Yes, please proceed.";
      await sendMessage({ text });
    },
    [sendMessage],
  );

  const declineAction = useCallback(async () => {
    await sendMessage({ text: "No, please don't do that." });
  }, [sendMessage]);

  /** Open the turn that actually applies a parked decision.
   *
   *  /steer only WRITES a decision — `resolve_budget_pause` and
   *  `resume_pending_if_decided` are both consumed on turn ENTRY, and there is
   *  no resume endpoint. So whoever posts a decision has to open that turn, or
   *  nothing happens until the user happens to type again.
   *
   *  Shared by decideBudget and decideReview because the ordering here is
   *  subtle and was got wrong once already (see the comments inside): the
   *  paused turn's rendered content must be promoted to history BEFORE
   *  beginTurn resets it, and beginTurn must run only AFTER the stream opens
   *  so a failed resume leaves that content standing instead of replacing it
   *  with a bare error.
   *
   *  Mirrors regenerate(): empty text and no optimistic user bubble, because a
   *  resume carries no new user intent to show. */
  const openResumeTurn = useCallback(
    async (sessionId: string) => {
      promoteHeldTurn(sessionId);

      // "continue" widens the budget on the backend but does nothing until a
      // turn actually re-enters the loop — resolve_budget_pause is consumed
      // on the NEXT turn's entry. Open that resume turn ourselves rather than
      // waiting for the user to type something. Mirrors regenerate(): no user
      // text, no optimistic user bubble (a budget continue has no user
      // intent to show) — just re-open the stream on the same session so the
      // backend's turn-entry picks up the pending decision.
      const message_id = crypto.randomUUID();
      setActiveMessageId(message_id);
      const signal = nextStreamSignal();
      try {
        const resp = await client.openChatStream({
          text: "",
          session_id: sessionId,
          message_id,
        }, signal);
        if (signal.aborted) return;
        // Only once the stream is OPEN. This used to run before the request,
        // so a resume that threw left an emptied bubble showing nothing but
        // the error — the artifact gone and an error in its place, which is
        // how the bug was reported. Beginning the turn after the await means
        // a failure leaves the finalized turn (artifact included) standing.
        beginTurnRef.current();
        const gen = parseSSEStream(resp.body!);
        setStream(gen);
      } catch (e) {
        if (signal.aborted) return;
        const msg =
          e instanceof Error ? e.message : "Failed to resume the turn.";
        setStream(errorStream(msg));
      }
    },
    [client, promoteHeldTurn, nextStreamSignal],
  );

  const decidePlan = useCallback(
    async (
      decision: "approve" | "edit" | "reject",
      opts?: { edited_todos?: unknown[]; reason?: string },
    ) => {
      if (!sessionId) return;
      await client.planDecision(sessionId, decision, opts);
    },
    [client, sessionId],
  );

  const decideReview = useCallback(
    async (decision: "approve" | "reject", opts?: { reason?: string }) => {
      // turnRef, not `turn`: reading the live turn as a dependency would
      // re-create this callback on every streamed chunk (same reason
      // decideBudget uses it).
      const review = turnRef.current.pendingReview;
      if (!sessionId || !review) return;
      if (review.error) {
        // FAIL CLOSED. The gate could not persist the pending call, so the
        // server has forgotten the question: a decision would be written to
        // Redis, never read by resume_pending_if_decided, and the user would
        // believe they approved a send that can never happen. Surface it
        // instead — the turn has to be retried, not decided.
        throw new Error(
          `Cannot decide ${review.tool_name}: the approval gate failed (${review.error}). ` +
            "The action did not run — retry the request.",
        );
      }
      await client.toolDecision(sessionId, review.call_id, decision, opts);
      // /steer only PARKS the verdict; `resume_pending_if_decided` consumes it
      // on turn entry and there is no resume endpoint. Without this the click
      // did nothing visible and the send happened whenever the user next
      // typed — which reads as the button not working, then as an email going
      // out on its own later.
      //
      // BOTH verdicts, not just approve. A rejection is equally parked, so
      // without it the model cannot acknowledge the refusal until the next
      // message either, and the card would sit there looking unanswered.
      await openResumeTurn(sessionId);
    },
    [client, sessionId, openResumeTurn],
  );

  const decideBudget = useCallback(
    async (decision: "continue" | "stop") => {
      if (!sessionId) return;
      await client.budgetDecision(sessionId, decision);
      if (decision !== "continue") {
        // Stop: the paused turn is held out of Complete (so its completed-so-
        // far content, e.g. a finished artifact, stays visible during the
        // pause instead of vanishing). Finalize it now so it promotes to
        // history as the turn's result.
        turn.finalizeBudgetStop();
        return;
      }
      // FINALIZE BEFORE RESUMING. The paused turn is held out of Complete so
      // its completed-so-far content stays visible during the pause — but the
      // promote-to-history effect only fires on Complete, so that content
      // lives ONLY in the live turn. `beginTurn()` below dispatches
      // `connected`, which resets to initialTurnState
      // (useStreamingTurn.ts:405) and takes the artifact with it.
      //
      // The symptom was an artifact vanishing the moment Continue was clicked,
      // recoverable only by refreshing the whole page (which re-reads server
      // history). Stop never showed it because it calls finalizeBudgetStop and
      // then RETURNS — React re-renders, the promote effect sees Complete, and
      // the content lands in history.
      //
      // Continue cannot wait for that. finalizeBudgetStop and beginTurn both
      // dispatch synchronously in the same batch, so the effect never observes
      // the intermediate Complete — it only ever sees the post-reset state.
      // So the paused turn's parts are appended here, directly, rather than
      // through the effect.
      await openResumeTurn(sessionId);
    },
    [client, sessionId],
  );

  const patchProposalStatus = useCallback(
    (proposal_id: string, status: ProposalPseudoMessage["status"]) => {
      setHistory((prev) =>
        prev.map((e) =>
          e.role === "proposal" && e.id === proposal_id ? { ...e, status } : e,
        ),
      );
    },
    [],
  );

  const acceptProposal = useCallback(
    async (proposal_id: string) => {
      if (!proposalsApi) {
        throw new Error(
          "acceptProposal called without a proposalsApi instance — wire one " +
            "through useChatSession's proposalsApi arg.",
        );
      }
      patchProposalStatus(proposal_id, "accepted");
      try {
        await proposalsApi.accept(proposal_id);
      } catch (exc) {
        patchProposalStatus(proposal_id, "pending");
        throw exc;
      }
    },
    [proposalsApi, patchProposalStatus],
  );

  const rejectProposal = useCallback(
    async (proposal_id: string, reason?: string) => {
      if (!proposalsApi) {
        throw new Error(
          "rejectProposal called without a proposalsApi instance — wire one " +
            "through useChatSession's proposalsApi arg.",
        );
      }
      patchProposalStatus(proposal_id, "rejected");
      try {
        await proposalsApi.reject(proposal_id, reason);
      } catch (exc) {
        patchProposalStatus(proposal_id, "pending");
        throw exc;
      }
    },
    [proposalsApi, patchProposalStatus],
  );

  const submitFeedback = useCallback(
    async (messageId: string, vote: "up" | "down" | null) => {
      if (!sessionId) return;
      // Optimistic toggle on the matching assistant entry; revert on failure.
      let prevVote: "up" | "down" | null = null;
      setHistory((prev) =>
        prev.map((e) => {
          if (e.role === "assistant" && e.id === messageId) {
            prevVote =
              (e as { feedback?: "up" | "down" | null }).feedback ?? null;
            return { ...e, feedback: vote };
          }
          return e;
        }),
      );
      try {
        await client.sendMessageFeedback(sessionId, messageId, vote);
      } catch {
        const revertTo = prevVote;
        setHistory((prev) =>
          prev.map((e) =>
            e.role === "assistant" && e.id === messageId
              ? { ...e, feedback: revertTo }
              : e,
          ),
        );
      }
    },
    [client, sessionId],
  );

  return useMemo(
    () => ({
      history,
      turn,
      sendMessage,
      cancel,
      steer,
      regenerate,
      editAndResend,
      availableCommands,
      acceptProposal,
      rejectProposal,
      confirmAction,
      declineAction,
      decidePlan,
      decideBudget,
      decideReview,
      submitFeedback,
      reloadHistory,
      historyTruncated,
      loadEarlier,
    }),
    [
      history,
      turn,
      sendMessage,
      cancel,
      steer,
      regenerate,
      editAndResend,
      availableCommands,
      acceptProposal,
      rejectProposal,
      confirmAction,
      declineAction,
      decidePlan,
      decideBudget,
      decideReview,
      submitFeedback,
      reloadHistory,
      historyTruncated,
      loadEarlier,
    ],
  );
}
